import { Instagram, Twitter, Youtube } from '@/assets/icons';
import logoWhite from '@/assets/logo-white.svg';
import logoWhiteArabic from '@/assets/logo-white-arabic.png';
import tajawob from '@/assets/tajawob.png';
import { Phone } from 'lucide-react';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { FooterLinkProps } from '@/lib/types';
import { Link as RouterLink } from '@tanstack/react-router';
import Linkedin from '@/assets/icons/Linkedin';
import Facebook from '@/assets/icons/Facebook';
import { useAnalytics } from '@/hooks/useAnalytics';

const FooterLink = ({ to, external, children, openInNewTab, ...props }: ComponentProps<'a'> & FooterLinkProps) => {
  if (external || !to) {
    return (
      <a
        href={to || '#'}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        className="text-inverted-link underline underline-offset-8"
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      to={to}
      onClick={() => window.scrollTo(0, 0)}
      className="text-inverted-link underline underline-offset-8"
      {...props}
    >
      {children}
    </RouterLink>
  );
};

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();

  return (
    <footer className="border-t-8 border-[#61AEE5] bg-[#001E30] px-3">
      <div className="max-w-[960px] mx-auto py-8 ">
        {/* Important Numbers Section */}
        <div className="flex flex-wrap md:grid md:grid-cols-[2fr_1fr_1fr_1fr] gap-8 mb-12 items-center">
          <div className="flex items-center gap-4">
            <div
              className="flex items-center justify-center rounded-full text-white text-[28px]"
              style={{
                background: '#2A324C',
                width: '64px',
                height: '64px',
              }}
            >
              <Phone size={28} />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-lg text-inverted-primary font-bold">{t('footer.importantNumbers')}</span>
              <FooterLink to="/contact-us" className="text-inverted-link underline underline-offset-8">
                {t('footer.viewAllNumbers')}
              </FooterLink>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-base text-inverted-secondary">{t('footer.callCentre')}</span>
            <a href="tel:1919" className="text-lg text-inverted-primary font-bold">
              1919
            </a>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-base text-inverted-secondary">{t('footer.phone')}</span>
            <a href="tel:24507500" className="text-lg text-inverted-primary font-bold">
              24507500
            </a>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-base text-inverted-secondary">{t('footer.internationalCalls')}</span>
            <a href="tel:80011110" className="text-lg text-inverted-primary font-bold">
              80011110
            </a>
          </div>
        </div>

        <hr className="border-inverted-xlow/[0.12] my-12"></hr>

        <div className="flex flex-wrap gap-6">
          <div className="flex-1 flex flex-col gap-6">
            <span className="text-xl text-inverted-primary font-bold">{t('footer.categories')}</span>
            <div className="flex flex-col gap-3">
              <FooterLink to="/services">{t('footer.services')}</FooterLink>
              <FooterLink to="/dashboard/permits">{t('footer.permits')}</FooterLink>
              <FooterLink to="/dashboard/contracts">{t('footer.contracts')}</FooterLink>
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-6">
            <span className="text-xl text-inverted-primary font-bold">{t('footer.explore')}</span>
            <div className="flex flex-col gap-3">
              <FooterLink to="/">{t('footer.home')}</FooterLink>
              <FooterLink external openInNewTab to="https://www.opaz.gov.om/en/why-oman">
                {t('footer.investmentOpportunities')}
              </FooterLink>
              <FooterLink external openInNewTab to="https://omap.om/">
                {t('footer.omap')}
              </FooterLink>
              <FooterLink to="/about-us">{t('footer.aboutOpaz')}</FooterLink>
              <FooterLink external openInNewTab to="https://internal.zoneservices.gov.om">
                {t('footer.zonesEmployees')}
              </FooterLink>
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-6">
            <span className="text-xl text-inverted-primary font-bold">{t('footer.discoverZones')}</span>
            <div className="flex flex-col gap-3">
              <FooterLink external openInNewTab to="https://soharportandfreezone.om">
                {t('footer.soharFreezone')}
              </FooterLink>
              <FooterLink external openInNewTab to="https://duqm.gov.om/en">
                {t('footer.specialEconomicZone')}
              </FooterLink>
              <FooterLink external openInNewTab to="https://www.sfzco.com/">
                {t('footer.salalahFreezone')}
              </FooterLink>
              <FooterLink external openInNewTab to="https://khazaen.om/">
                {t('footer.khazaenEconomicCity')}
              </FooterLink>
              <FooterLink external openInNewTab to="https://www.madayn.om/sites/AFZ/EN/Pages/home.aspx">
                {t('footer.alMazunahFreezone')}
              </FooterLink>
              <FooterLink external openInNewTab to="https://madayn.om/">
                {t('footer.industrialEstate')}
              </FooterLink>
            </div>
          </div>
        </div>

        <hr className="border-inverted-xlow/[0.12] my-12"></hr>

        <div className="flex flex-col-reverse items-center lg:flex-row lg:justify-between lg:items-center">
          <div className="flex gap-3">
            <FooterLink
              id="social-link"
              external
              openInNewTab
              to="https://www.youtube.com/channel/UCuMqLcSCixwDsHvQCRoDjEw"
              onClick={() => analytics.trackSocialLinkClick('youtube')}
              className="flex items-center justify-center text-muted rounded-full border border-solid border-muted w-10 h-10"
            >
              <Youtube />
            </FooterLink>

            <FooterLink
              id="social-link"
              external
              openInNewTab
              to="https://www.instagram.com/omanopaz"
              onClick={() => analytics.trackSocialLinkClick('instagram')}
              className="flex items-center justify-center text-muted rounded-full border border-solid border-muted w-10 h-10"
            >
              <Instagram />
            </FooterLink>

            <FooterLink
              id="social-link"
              external
              openInNewTab
              to="https://twitter.com/OmanOpaz"
              onClick={() => analytics.trackSocialLinkClick('twitter')}
              className="flex items-center justify-center text-muted rounded-full border border-solid border-muted w-10 h-10"
            >
              <Twitter />
            </FooterLink>

            <FooterLink
              id="social-link"
              external
              openInNewTab
              to="https://www.linkedin.com/company/omanopaz"
              onClick={() => analytics.trackSocialLinkClick('linkedin')}
              className="flex items-center justify-center text-muted rounded-full border border-solid border-muted w-10 h-10"
            >
              <Linkedin />
            </FooterLink>

            <FooterLink
              id="social-link"
              external
              openInNewTab
              to="https://www.facebook.com/omanopaz"
              onClick={() => analytics.trackSocialLinkClick('facebook')}
              className="flex items-center justify-center text-muted rounded-full border border-solid border-muted w-10 h-10"
            >
              <Facebook />
            </FooterLink>
          </div>

          <div className="flex items-center gap-2">
            <img
              src={tajawob}
              alt="Zone Services logo"
              className="w-32 sm:w-48 cursor-pointer"
              onClick={() => window.open('https://tajawob.om/', '_blank')}
            />
            <img
              src={i18n.language === 'ar' ? logoWhiteArabic : logoWhite}
              alt="Zone Services logo"
              className="w-36 sm:w-56"
            />
          </div>
        </div>

        <div className="flex flex-wrap lg:justify-start justify-center gap-3 mt-12 mb-6 ">
          <FooterLink>{t('footer.help')}</FooterLink>
          <FooterLink to="/about-us">{t('footer.about')}</FooterLink>
          <FooterLink to="/contact-us">{t('footer.contact')}</FooterLink>
          <FooterLink external to="https://www.opaz.gov.om/en/privacy-policy">
            {t('footer.privacyPolicy')}
          </FooterLink>
          <FooterLink to="/feedback">{t('footer.feedbackHub')}</FooterLink>
          <FooterLink to="/accessibility">{t('footer.accessibility')}</FooterLink>
          <FooterLink to="/sitemap">{t('footer.sitemap')}</FooterLink>
        </div>

        <span
          className="block text-muted text-center lg:text-left"
          dangerouslySetInnerHTML={{ __html: `© ${t('footer.investorPortal')}` }}
        />
      </div>
    </footer>
  );
};
